import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DotsThreeVertical, PencilSimple, LinkBreak, Stethoscope, AddressBook } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import DoctorPicker from '@/components/DoctorPicker';
import DoctorForm from '@/components/DoctorForm';
import { DoctorContact } from '@/components/DoctorBits';
import { DoctorName, FreeText } from '@/components/Present';

function LinkForm({ patientId, initial, excludeIds, onDone }) {
    const [doctor, setDoctor] = useState(initial ? { provider_id: initial.provider_id, name: initial.name } : null);
    const [role, setRole] = useState(initial?.role || '');
    const [notes, setNotes] = useState(initial?.notes || '');
    const [busy, setBusy] = useState(false);
    const save = async () => {
        if (!doctor?.provider_id) { toast.error('Pick a doctor first'); return; }
        setBusy(true);
        try {
            const body = { role: role.trim() || null, notes: notes.trim() || null };
            if (initial?.link_id) await api.patch(`/patient-doctors/${initial.link_id}`, body);
            else await api.post(`/patients/${patientId}/doctors`, { provider_id: doctor.provider_id, ...body });
            toast.success(initial ? 'Saved' : 'Doctor linked');
            onDone?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3">
            <div>
                <Label>Doctor</Label>
                {initial ? (
                    <div className="text-sm font-medium text-slate-900 py-2"><DoctorName name={initial.name} />{initial.specialty ? <span className="text-slate-500 font-normal"> · {initial.specialty}</span> : null}</div>
                ) : (
                    <DoctorPicker patientId={patientId} value={doctor} onChange={setDoctor} testid="their-doctors-picker" excludeIds={excludeIds} allowClear={false} />
                )}
            </div>
            <div>
                <Label>Role for this person <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input data-testid="their-doctors-role" value={role} onChange={e => setRole(e.target.value)} placeholder='e.g., "Primary care", "Cardiology", "Dentist"' />
            </div>
            <div>
                <Label>Notes for this person <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea data-testid="their-doctors-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                    placeholder='e.g., "ask for Natali when scheduling", "portal login is under my email"' />
            </div>
            <DialogFooter>
                <Button data-testid="their-doctors-save" onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground min-h-[44px] sm:min-h-0">
                    {initial ? 'Save' : 'Link doctor'}
                </Button>
            </DialogFooter>
        </div>
    );
}

export default function TheirDoctors({ patientId }) {
    const qc = useQueryClient();
    const [addOpen, setAddOpen] = useState(false);
    const [editLink, setEditLink] = useState(null);
    const [editDoctor, setEditDoctor] = useState(null);
    const [unlink, setUnlink] = useState(null);
    const { data: rows = [] } = useQuery({
        queryKey: ['member-doctors', patientId],
        queryFn: async () => (await api.get(`/patients/${patientId}/doctors`)).data,
    });
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['member-doctors', patientId] });
        qc.invalidateQueries({ queryKey: ['doctors'] });
        qc.invalidateQueries({ queryKey: ['patient', patientId] });
    };
    const openEditDoctor = async (r) => {
        try { setEditDoctor((await api.get(`/doctors/${r.provider_id}`)).data); }
        catch { toast.error('Could not load doctor'); }
    };
    const doUnlink = async () => {
        try {
            await api.delete(`/patient-doctors/${unlink.link_id}`);
            toast.success('Removed from this member — still in your doctors list');
            setUnlink(null); refresh();
        } catch { toast.error('Failed'); }
    };
    const linkedIds = rows.map(r => r.provider_id).filter(Boolean);

    return (
        <div data-testid="their-doctors">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="text-sm text-slate-600" data-testid="their-doctors-count">{rows.length} doctor{rows.length === 1 ? '' : 's'}</div>
                <Button data-testid="their-doctors-add" size="sm" className="rounded-full bg-primary text-primary-foreground min-h-[44px] md:min-h-0" onClick={() => setAddOpen(true)}>
                    Add doctor
                </Button>
            </div>
            <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                {rows.length === 0 && (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">
                        <Stethoscope size={28} className="mx-auto text-slate-400 mb-2" />
                        No doctors linked yet — use “Add doctor” to pick one from your list or add a new one.
                    </div>
                )}
                {rows.map((r, i) => (
                    <div key={r.link_id || r.provider_id || i} data-testid="their-doctors-row" className="px-4 sm:px-5 py-4 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base font-medium text-slate-900" data-testid="their-doctors-name"><DoctorName name={r.name} /></span>
                                {r.is_primary && <span data-testid="their-doctors-primary" className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary">Primary</span>}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5" data-testid="their-doctors-role-text">
                                {[...new Set([r.role, r.specialty].filter(Boolean))].join(' · ') || 'Role not set'}
                            </div>
                            <div className="mt-2"><DoctorContact doctor={r} compact testidPrefix="their-doctors" /></div>
                            {r.notes && (
                                <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap" data-testid="their-doctors-notes-text">
                                    <FreeText>{r.notes}</FreeText>
                                </div>
                            )}
                            {!r.provider_id && <div className="mt-1 text-[11px] text-slate-400">Name only — set the primary doctor from Edit profile to link a record.</div>}
                        </div>
                        {r.provider_id && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button data-testid="their-doctors-menu" className="p-2 rounded hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1.5 flex items-center justify-center" aria-label="More"><DotsThreeVertical size={18} weight="bold" /></button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem data-testid="their-doctors-edit" onClick={() => setEditLink(r)}><PencilSimple size={14} className="mr-2" /> Edit role & notes</DropdownMenuItem>
                                    <DropdownMenuItem data-testid="their-doctors-edit-doctor" onClick={() => openEditDoctor(r)}><AddressBook size={14} className="mr-2" /> Edit contact details</DropdownMenuItem>
                                    {r.link_id && (
                                        <DropdownMenuItem data-testid="their-doctors-remove" onClick={() => setUnlink(r)} className="text-red-600 focus:text-red-700">
                                            <LinkBreak size={14} className="mr-2" /> Remove from this member
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                ))}
            </div>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Add a doctor</DialogTitle><DialogDescription>Link a doctor to this family member, with a role and notes just for them.</DialogDescription></DialogHeader>
                    <LinkForm patientId={patientId} excludeIds={linkedIds} onDone={() => { setAddOpen(false); refresh(); }} />
                </DialogContent>
            </Dialog>

            <Dialog open={!!editLink} onOpenChange={(v) => !v && setEditLink(null)}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit role & notes</DialogTitle><DialogDescription>These details are only for this family member.</DialogDescription></DialogHeader>
                    {editLink && <LinkForm patientId={patientId} initial={editLink} onDone={() => { setEditLink(null); refresh(); }} />}
                </DialogContent>
            </Dialog>

            <Dialog open={!!editDoctor} onOpenChange={(v) => !v && setEditDoctor(null)}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit contact details</DialogTitle><DialogDescription>Updates the doctor everywhere they appear.</DialogDescription></DialogHeader>
                    {editDoctor && <DoctorForm initial={editDoctor} onSaved={() => { setEditDoctor(null); refresh(); }} />}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!unlink} onOpenChange={(v) => !v && setUnlink(null)}
                title={`Remove ${unlink?.name || 'this doctor'} from this member?`}
                description="This only removes the link. The doctor stays in your doctors list and on anyone else they're linked to."
                confirmLabel="Remove link"
                onConfirm={doUnlink}
                testId="their-doctors-remove-confirm"
            />
        </div>
    );
}
